/**
 * CA navigation section ids mapped to localized URL segments (mirrors scripts/localized_paths_map.json).
 */

const SECTION_IDS = {
  'la-clinica': { ca: 'la-clinica', en: 'the-clinic', es: 'la-clinica' },
  pacients: { ca: 'pacients', en: 'patients', es: 'pacientes' },
  'serveis-medics': {
    ca: 'serveis-medics',
    en: 'medical-services',
    es: 'servicios-medicos',
  },
  actualitat: { ca: 'actualitat', en: 'news', es: 'actualidad' },
  contacte: { ca: 'contacte', en: 'contact', es: 'contacto' },
};

export const publicationFolderSegment = (folderKey, lang) => {
  const map = {
    noticies: {
      en: 'press-releases',
      es: 'noticias',
      ca: 'noticies',
    },
    blog: { en: 'blog', es: 'blog', ca: 'blog' },
  };
  return map[folderKey]?.[lang] || folderKey;
};

/** Plone folder ids that should use NewsFolderView (all languages). */
export const newsFolderContentIds = () =>
  (['ca', 'en', 'es']).map((lang) => publicationFolderSegment('noticies', lang));

const MEDICAL_FOLDER_SEGMENTS = {
  'quadre-medic': {
    ca: 'quadre-medic',
    en: 'medical-directory',
    es: 'cuadro-medico',
  },
  especialitats: {
    ca: 'especialitats',
    en: 'specialties',
    es: 'especialidades',
  },
  'unitats-especialitzades': {
    ca: 'unitats-especialitzades',
    en: 'specialized-units',
    es: 'unidades-especializadas',
  },
};

export const medicalFolderSegment = (folderKey, lang) => {
  const entry = MEDICAL_FOLDER_SEGMENTS[folderKey];
  if (!entry) {
    return folderKey;
  }
  return entry[lang] || entry.ca || folderKey;
};

const medicalFolderIdsForKey = (folderKey) => {
  const entry = MEDICAL_FOLDER_SEGMENTS[folderKey];
  if (!entry) {
    return [folderKey];
  }
  return [...new Set(Object.values(entry))];
};

export const quadreMedicFolderContentIds = () =>
  medicalFolderIdsForKey('quadre-medic');

export const quadreMedicFolderAlternation = medicalFolderIdsForKey(
  'quadre-medic',
).join('|');

export const especialitatsFolderContentIds = () =>
  medicalFolderIdsForKey('especialitats');

export const unitatsEspecialitzadesFolderContentIds = () =>
  medicalFolderIdsForKey('unitats-especialitzades');

/** Curated static pages (CA path suffix -> localized suffix per lang). */
const PAGE_SUFFIXES = {
  '/la-clinica/certificacio-efqm': {
    en: '/the-clinic/efqm-certification',
    es: '/la-clinica/certificacion-efqm',
  },
  '/la-clinica/lliure-de-fum': {
    en: '/the-clinic/smoke-free',
    es: '/la-clinica/libre-de-humo',
  },
  '/pacients/com-ingressar': {
    en: '/patients/how-to-be-admitted',
    es: '/pacientes/como-ingresar',
  },
  '/legal': {
    en: '/legal-notice',
    es: '/aviso-legal',
  },
  '/politica-privacitat': {
    en: '/privacy-policy',
    es: '/politica-de-privacidad',
  },
  '/sitemap': {
    en: '/site-map',
    es: '/mapa-del-sitio',
  },
};

export const sectionIdForLang = (caSectionId, lang) => {
  const entry = SECTION_IDS[caSectionId];
  if (!entry) {
    return caSectionId;
  }
  return entry[lang] || entry.ca || caSectionId;
};

/**
 * Turn a CA-relative path (/la-clinica/...) into a Volto path for the given language.
 */
export const localizeCaRelativePath = (lang, caRelativePath) => {
  const normalized = caRelativePath.startsWith('/')
    ? caRelativePath
    : `/${caRelativePath}`;
  if (lang === 'ca') {
    return `/ca${normalized}`;
  }
  const page = PAGE_SUFFIXES[normalized];
  if (page?.[lang]) {
    return `/${lang}${page[lang]}`;
  }
  const originalParts = normalized.replace(/^\//, '').split('/');
  const parts = [...originalParts];
  if (parts.length >= 1 && parts[0]) {
    parts[0] = sectionIdForLang(parts[0], lang);
  }
  if (parts.length >= 2 && MEDICAL_FOLDER_SEGMENTS[originalParts[1]]) {
    parts[1] = medicalFolderSegment(originalParts[1], lang);
  }
  const changed = parts.some((part, index) => part !== originalParts[index]);
  if (changed) {
    return `/${lang}/${parts.join('/')}`;
  }
  return `/${lang}${normalized}`;
};

export const medicalServicesSegment = (lang) =>
  sectionIdForLang('serveis-medics', lang);

/** URL segment alternation for all languages (Volto routes, breadcrumbs, redirects). */
export const medicalServicesSectionAlternation =
  'serveis-medics|medical-services|servicios-medicos';
